/* eslint-disable no-nested-ternary */
import { FC, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from 'react-query';
import { platform, utils } from 'network-utils';
import { translate } from 'translations';
import { useDispatch } from 'react-redux';
import { Loader } from 'learning-panel-components';
import { setIsPlatformAuthenticated, setCustomBrandName, selectIsCxUniversity } from '@redux/slices/userSlice';
import { getHomepage } from '@services/getHomepage';
import { showAlert } from '@redux/slices/informationSlice';
import { useGetIsTenantLoginStrategy } from '@queries/platform/login';
import LoginWrapper from './loginWrapper/loginWrapper';
import LoginSSO from './loginSso/loginSso';
import Login from './login';

const LoginContainer: FC = () => {
    const services = platform.LoginApi;
    const [error, setError] = useState<string>('');
    const [isSso, setIsSso] = useState<boolean>(false);
    const [isPLG, setIsPLG] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const generalError = translate('Something went wrong, please try again later');
    const { data: dataLoginStrategy, isLoading: isSsoLoading } = useGetIsTenantLoginStrategy();

    useEffect(() => {
        if (!dataLoginStrategy?.success) return;
        setIsSso(dataLoginStrategy.strategy === 'sso_strategy');
        setIsPLG(dataLoginStrategy.is_plg_tenant);
        dispatch(setCustomBrandName(dataLoginStrategy.custom_brand_name));
    }, [dataLoginStrategy]);

    useEffect(() => {
        const errorParam = new URLSearchParams(location.search).get('error');
        if (errorParam) {
            dispatch(
                showAlert({
                    value: errorParam,
                    status: 'error'
                })
            );
        }
    }, [dispatch, location.search]);

    const submitLogin = async (input: {
        user: { email: string; password: string };
    }): Promise<platform.PostLoginResponse> => {
        const postLoginResponse = await services.postLogin(input);
        return postLoginResponse;
    };

    const submitLoginToGoogle = async (input: { token: string }): Promise<platform.PostWithGoogleLoginResponse> => {
        const postLoginResponse = await services.loginWithGoogle(input);
        return postLoginResponse;
    };

    const { mutate, isLoading } = useMutation(submitLogin, {
        onSuccess: (postLoginResponse: platform.PostLoginResponse) => {
            if (postLoginResponse.json.success) {
                dispatch(setIsPlatformAuthenticated(true));
                const role = utils.getRole(postLoginResponse.json);
                const navTo = sessionStorage.getItem('cbHistory') || getHomepage(role);
                sessionStorage.removeItem('cbHistory');

                if (window.top) {
                    window.top.location.href = navTo;
                }
                return;
            }

            setError(postLoginResponse.json.errors[0]);
        },
        onError: () => {
            setError(generalError);
        }
    });

    const { mutate: mutateLoginToGoogle, isLoading: isLoadingToGoogle } = useMutation(submitLoginToGoogle, {
        onSuccess: (postLoginResponse: platform.PostWithGoogleLoginResponse) => {
            if (postLoginResponse.success) {
                if (postLoginResponse.user_existed) {
                    dispatch(setIsPlatformAuthenticated(true));
                    navigate('/app/home');
                } else {
                    navigate(`/app/user-details/${postLoginResponse.confirmation_token}?eula=true`);
                }
            } else {
                navigate('/app/not-found');
            }
        },
        onError: () => {
            setError(generalError);
        }
    });

    const onLogin = (email: string, password: string) => {
        setError('');
        mutate({ user: { email, password } });
    };

    const onLoginToGoogle = (token: string) => {
        mutateLoginToGoogle({ token });
    };

    const onCreateNewAccount = () => {
        navigate('/app/create-account');
    };

    return (
        <LoginWrapper height={isSso ? '10rem' : isPLG ? '41rem' : '30.75rem'} isLogin>
            {!isSsoLoading ? (
                <>
                    {!isSso ? (
                        <Login
                            onLogin={onLogin}
                            onForgetPassword={() => navigate('/app/forget-password')}
                            isLoading={isLoading || isLoadingToGoogle}
                            error={error}
                            isPLG={isPLG}
                            onCreateNewAccount={onCreateNewAccount}
                            onLoginToGoogle={onLoginToGoogle}
                        />
                    ) : (
                        <LoginSSO error={error} />
                    )}
                </>
            ) : (
                <Loader />
            )}
        </LoginWrapper>
    );
};

export default LoginContainer;
